/**
 * ConnectMyHub™ brand primitives — inline SVG. No image assets, no network fetch.
 * Exports:
 *   - InfinityMark  → the master brand icon (blue→teal infinity)
 *   - BrandTagline  → coloured "People · Skills · Business · Connected"
 *   - BrandLockup   → full expanded lockup used in the sidebar
 *   - ConnectMyHubLogo → generic wordmark (dark/light/mark)
 */
export function InfinityMark({ size = 32, className = "" }) {
  // Renders the OFFICIAL ConnectMyHub™ approved brand mark asset (blue-to-teal
  // connected infinity). Do NOT replace this with a CSS/SVG approximation.
  return (
    <img
      src="/brand/mark-only.png"
      alt="ConnectMyHub"
      width={size}
      height={Math.round(size * 106 / 163)}
      className={className}
      style={{ display: "inline-block", objectFit: "contain" }}
    />
  );
}

export function BrandTagline({ className = "", size = "xs" }) {
  const s = size === "xxs" ? "text-[8px] tracking-[0.16em]"
          : size === "sm"  ? "text-[11px] tracking-[0.22em]"
          :                  "text-[9px] tracking-[0.18em]";
  return (
    <div className={`font-semibold uppercase ${s} ${className}`}>
      <span className="text-white">People.</span>{" "}
      <span className="text-teal-300">Skills.</span>{" "}
      <span className="text-sky-300">Business.</span>{" "}
      <span className="text-teal-300">Connected.</span>
      <span className="text-slate-400 ml-0.5">™</span>
    </div>
  );
}

/**
 * Sidebar-native lockup: infinity mark on top, wordmark, "by My Pipeline Solutions", tagline.
 * Sits directly on the navy sidebar — no boxed background.
 */
export function BrandLockup({ compact = false }) {
  if (compact) return <InfinityMark size={48} />;
  return (
    <div className="flex flex-col items-center text-center gap-1.5">
      <InfinityMark size={72} />
      <div className="font-display font-bold text-white text-[17px] leading-none tracking-tight mt-1">
        Connect<span className="text-teal-300">My</span>Hub
        <sup className="text-[8px] font-semibold ml-0.5 opacity-80">™</sup>
      </div>
      <div className="text-[8px] font-semibold uppercase tracking-[0.16em] text-slate-400">by My Pipeline Solutions</div>
      <BrandTagline size="xxs" />
    </div>
  );
}

export function ConnectMyHubLogo({ variant = "dark", tagline = false, size = "md", className = "" }) {
  const dims = size === "sm" ? { mark: 22, text: "text-sm" }
             : size === "lg" ? { mark: 40, text: "text-2xl" }
             :                  { mark: 28, text: "text-base" };
  const textColor = variant === "light" ? "text-slate-900" : "text-white";
  if (variant === "mark") return <InfinityMark size={dims.mark} className={className} />;
  return (
    <div className={`inline-flex items-center gap-2.5 ${className}`}>
      <InfinityMark size={dims.mark} />
      <div className="leading-none">
        <div className={`font-display font-bold tracking-tight ${dims.text} ${textColor}`}>
          Connect<span className="text-teal-300">My</span>Hub<sup className="text-[9px] font-semibold ml-0.5 opacity-80">™</sup>
        </div>
        {tagline && <div className="mt-1"><BrandTagline size="xxs" /></div>}
      </div>
    </div>
  );
}

export default ConnectMyHubLogo;
